import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';

// ---- Types ----

export interface UserGrowthRow {
  date: string;
  new_startups: number;
  new_investors: number;
  cumulative_startups: number;
  cumulative_investors: number;
}

export interface FunnelStage {
  label: string;
  count: number;
}

export interface RetentionCohort {
  cohort_week: string;
  cohort_size: number;
  w1_retention: number;
  w2_retention: number;
  w3_retention: number;
  w4_retention: number;
}

export interface MatchingFunnelRow {
  stage: string;
  count: number;
}

export interface SideBalance {
  total_startups: number;
  total_investors: number;
  active_startups: number;
  active_investors: number;
  ratio: number;
}

export interface EngagementFeature {
  feature_name: string;
  total_events: number;
  unique_users: number;
  events_per_user: number;
}

export interface SessionAnalytics {
  platform: string;
  total_sessions: number;
  avg_duration_min: number;
  events_per_session: number;
}

export interface ExecutiveKPIs {
  total_users: number;
  mau: number;
  activation_rate: number;
  avg_inverio_score: number;
}

export interface ExecutiveDashboardData {
  kpis: ExecutiveKPIs;
  userGrowth: UserGrowthRow[];
  startupFunnel: FunnelStage[];
  investorFunnel: FunnelStage[];
  retentionCohorts: RetentionCohort[];
  sideBalance: SideBalance;
  matchingFunnel: FunnelStage[];
  featureEngagement: EngagementFeature[];
  sessionAnalytics: SessionAnalytics[];
}

// ---- Mock Data ----

function generateMockGrowth(): UserGrowthRow[] {
  const rows: UserGrowthRow[] = [];
  let cumS = 40;
  let cumI = 20;
  const now = Date.now();
  for (let i = 89; i >= 0; i--) {
    const d = new Date(now - i * 86400000);
    const date = d.toISOString().split('T')[0];
    const ns = Math.floor(Math.random() * 4) + (i < 30 ? 2 : 1);
    const ni = Math.floor(Math.random() * 3) + (i < 30 ? 1 : 0);
    cumS += ns;
    cumI += ni;
    rows.push({ date, new_startups: ns, new_investors: ni, cumulative_startups: cumS, cumulative_investors: cumI });
  }
  return rows;
}

const mockData: ExecutiveDashboardData = {
  kpis: {
    total_users: 284,
    mau: 156,
    activation_rate: 68.3,
    avg_inverio_score: 62.4,
  },
  userGrowth: generateMockGrowth(),
  startupFunnel: [
    { label: 'Signed Up', count: 180 },
    { label: 'Profile Done', count: 142 },
    { label: 'Score Calculated', count: 98 },
    { label: 'Data Room Created', count: 67 },
    { label: 'Matched', count: 34 },
  ],
  investorFunnel: [
    { label: 'Signed Up', count: 104 },
    { label: 'Profile Done', count: 89 },
    { label: 'Criteria Set', count: 71 },
    { label: 'Dealflow Viewed', count: 52 },
    { label: 'Invested', count: 12 },
  ],
  retentionCohorts: [
    { cohort_week: '2026-01-06', cohort_size: 24, w1_retention: 75, w2_retention: 58, w3_retention: 42, w4_retention: 33 },
    { cohort_week: '2026-01-13', cohort_size: 31, w1_retention: 71, w2_retention: 52, w3_retention: 39, w4_retention: 29 },
    { cohort_week: '2026-01-20', cohort_size: 28, w1_retention: 79, w2_retention: 61, w3_retention: 45, w4_retention: 0 },
    { cohort_week: '2026-01-27', cohort_size: 35, w1_retention: 83, w2_retention: 57, w3_retention: 0, w4_retention: 0 },
    { cohort_week: '2026-02-03', cohort_size: 22, w1_retention: 68, w2_retention: 0, w3_retention: 0, w4_retention: 0 },
  ],
  sideBalance: {
    total_startups: 180,
    total_investors: 104,
    active_startups: 98,
    active_investors: 52,
    ratio: 1.73,
  },
  matchingFunnel: [
    { label: 'New Matches', count: 320 },
    { label: 'Reviewing', count: 186 },
    { label: 'Interested', count: 94 },
    { label: 'Passed', count: 42 },
    { label: 'Invested', count: 12 },
  ],
  featureEngagement: [
    { feature_name: 'Inverio Score', total_events: 1240, unique_users: 142, events_per_user: 8.7 },
    { feature_name: 'Data Room', total_events: 890, unique_users: 67, events_per_user: 13.3 },
    { feature_name: 'Messaging', total_events: 760, unique_users: 89, events_per_user: 8.5 },
    { feature_name: 'DD Assistant', total_events: 540, unique_users: 52, events_per_user: 10.4 },
    { feature_name: 'Dealflow', total_events: 480, unique_users: 48, events_per_user: 10.0 },
    { feature_name: 'Portfolio', total_events: 320, unique_users: 34, events_per_user: 9.4 },
    { feature_name: 'Recommendations', total_events: 280, unique_users: 98, events_per_user: 2.9 },
    { feature_name: 'Company Profile', total_events: 210, unique_users: 112, events_per_user: 1.9 },
  ],
  sessionAnalytics: [
    { platform: 'iOS', total_sessions: 1842, avg_duration_min: 8.4, events_per_session: 12.3 },
    { platform: 'Android', total_sessions: 946, avg_duration_min: 6.7, events_per_session: 9.8 },
    { platform: 'Web', total_sessions: 2314, avg_duration_min: 14.2, events_per_session: 18.1 },
  ],
};

// ---- Hook ----

export function useExecutiveDashboard() {
  return useQuery<ExecutiveDashboardData>({
    queryKey: ['executive-dashboard'],
    queryFn: async () => {
      if (mode.showSampleData) {
        return mockData;
      }

      const [
        kpisRes,
        growthRes,
        startupFunnelRes,
        investorFunnelRes,
        retentionRes,
        balanceRes,
        matchingRes,
        engagementRes,
        sessionsRes,
      ] = await Promise.all([
        supabase.from('v_executive_kpis').select('*').single(),
        supabase.from('v_user_growth').select('*').order('date', { ascending: true }),
        supabase.from('v_startup_funnel').select('*'),
        supabase.from('v_investor_funnel').select('*'),
        supabase.from('v_retention_cohorts').select('*').order('cohort_week', { ascending: true }),
        supabase.from('v_side_balance').select('*').single(),
        supabase.from('v_matching_funnel').select('*'),
        supabase.from('v_engagement_by_feature').select('*').order('total_events', { ascending: false }).limit(8),
        supabase.from('v_session_analytics').select('*'),
      ]);

      // Map SQL view column names to TypeScript interface field names
      let cumS = 0;
      let cumI = 0;
      const userGrowth: UserGrowthRow[] = (growthRes.data ?? []).map((r) => {
        cumS += r.startup_signups ?? 0;
        cumI += r.investor_signups ?? 0;
        return {
          date: r.date ?? '',
          new_startups: r.startup_signups ?? 0,
          new_investors: r.investor_signups ?? 0,
          cumulative_startups: cumS,
          cumulative_investors: cumI,
        };
      });

      const bal = balanceRes.data;
      const sideBalance: SideBalance = bal ? {
        total_startups: bal.startup_count ?? 0,
        total_investors: bal.investor_count ?? 0,
        active_startups: bal.active_startups_7d ?? 0,
        active_investors: bal.active_investors_7d ?? 0,
        ratio: bal.startup_to_investor_ratio ?? 0,
      } : mockData.sideBalance;

      const featureEngagement: EngagementFeature[] = (engagementRes.data ?? []).map((r) => ({
        feature_name: r.feature ?? '',
        total_events: r.total_events ?? 0,
        unique_users: r.unique_users ?? 0,
        events_per_user: r.events_per_user ?? 0,
      }));

      const sessionAnalytics: SessionAnalytics[] = (sessionsRes.data ?? []).map((r) => ({
        platform: r.platform ?? '',
        total_sessions: r.total_sessions ?? 0,
        avg_duration_min: r.avg_duration_seconds != null ? Math.round(r.avg_duration_seconds / 60 * 10) / 10 : 0,
        events_per_session: r.avg_events_per_session ?? 0,
      }));

      // Map KPIs — activation_rate is not in the SQL view, derive from onboarding data
      const k = kpisRes.data;
      const totalUsers = k?.total_users ?? 0;
      const kpis: ExecutiveKPIs = k ? {
        total_users: totalUsers,
        mau: k.mau ?? 0,
        activation_rate: totalUsers > 0
          ? Math.round((k.signups_30d ?? 0) / totalUsers * 1000) / 10
          : 0,
        avg_inverio_score: k.avg_inverio_score ?? 0,
      } : mockData.kpis;

      // Map retention cohorts — SQL returns raw counts, convert to percentages
      const retentionCohorts: RetentionCohort[] = (retentionRes.data ?? []).map((r) => {
        const size = r.cohort_size ?? 1;
        return {
          cohort_week: r.cohort_week ?? '',
          cohort_size: size,
          w1_retention: size > 0 ? Math.round((r.w1_retained ?? 0) / size * 100) : 0,
          w2_retention: size > 0 ? Math.round((r.w2_retained ?? 0) / size * 100) : 0,
          w3_retention: size > 0 ? Math.round((r.w3_retained ?? 0) / size * 100) : 0,
          w4_retention: size > 0 ? Math.round((r.w4_retained ?? 0) / size * 100) : 0,
        };
      });

      return {
        kpis,
        userGrowth,
        startupFunnel: (startupFunnelRes.data ?? []).map((r) => ({ label: r.stage ?? '', count: r.count ?? 0 })),
        investorFunnel: (investorFunnelRes.data ?? []).map((r) => ({ label: r.stage ?? '', count: r.count ?? 0 })),
        retentionCohorts,
        sideBalance,
        matchingFunnel: (matchingRes.data ?? []).map((r) => ({ label: r.status ?? '', count: r.total ?? 0 })),
        featureEngagement,
        sessionAnalytics,
      } satisfies ExecutiveDashboardData;
    },
    staleTime: 5 * 60 * 1000,
  });
}
